import React from 'react';
import { Box, DialogContent, Typography, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GGDialog, GGDialogTitle } from './StyledDialogs';
import {
  formatArmorClass,
  formatHitPoints,
  formatSpeed,
  formatAbilityScores,
  formatCreatureTypeLine,
  formatNamedBlocks,
  getSavingThrowsEntry,
  getSkillsEntry,
  getCommaListEntry,
  getSensesEntry,
  getLanguagesEntry,
  getChallengeEntry,
} from '../../utils/monsterStatBlock';

const Body = styled(DialogContent)(({ theme }) => ({
  paddingTop: theme.spacing(1.5),
  maxHeight: '75vh',
  overflowY: 'auto',
}));

const HeroRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  alignItems: 'stretch',
  marginBottom: theme.spacing(2),
}));

const HeroLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: '0 1 140px',
  minWidth: 120,
  gap: theme.spacing(1.25),
}));

const PortraitFrame = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 130,
  marginTop: theme.spacing(1),
  aspectRatio: '1',
  borderRadius: 10,
  overflow: 'hidden',
  border: `2px solid ${theme.palette.background.dark}`,
  backgroundColor: theme.palette.background.dark,
  boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
  flexShrink: 0,
}));

const PortraitImg = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
});

const PortraitPlaceholder = styled(Box)(({ color }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: color,
}));

const CreatureSub = styled(Typography)(({ theme }) => ({
  fontFamily: 'Merriweather',
  fontSize: '0.82rem',
  fontStyle: 'italic',
  color: theme.palette.text.secondary,
  lineHeight: 1.45,
  width: '100%',
  maxWidth: 220,
}));

const HeroRight = styled(Box)(({ theme }) => ({
  flex: '1 1 200px',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: theme.spacing(2),
}));

const TopStatGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const TopStatCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  minHeight: 76,
  padding: theme.spacing(1, 0.75),
  backgroundColor: theme.palette.background.main,
  borderRadius: 8,
  border: `1px solid ${theme.palette.background.dark}`,
  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04)`,
}));

const TopStatLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Merriweather',
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: '0.12em',
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  marginBottom: theme.spacing(0.5),
  lineHeight: 1.2,
}));

const TopStatValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'MedievalSharp',
  fontSize: '1.05rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: 1.25,
  wordBreak: 'break-word',
  padding: theme.spacing(0, 0.25),
}));

const DetailBlock = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.background.dark}`,
  borderRadius: 8,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.main,
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(100px, 34%) 1fr',
  gap: theme.spacing(1, 2),
  alignItems: 'start',
  padding: theme.spacing(0.9, 1.25),
  borderBottom: `1px solid ${theme.palette.background.dark}`,
  '&:last-of-type': {
    borderBottom: 'none',
  },
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Merriweather',
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  lineHeight: 1.45,
  paddingTop: 2,
}));

const DetailValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Merriweather',
  fontSize: '0.84rem',
  color: theme.palette.text.primary,
  lineHeight: 1.55,
  wordBreak: 'break-word',
}));

const AbilitySection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));

const AbilityGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: theme.spacing(1),
}));

const AbilityCell = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(1, 0.5),
  minHeight: 64,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.main,
  borderRadius: 8,
  border: `1px solid ${theme.palette.background.dark}`,
  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.03)`,
}));

const AbilityAbbr = styled(Typography)(({ theme }) => ({
  fontFamily: 'Merriweather',
  fontSize: '0.62rem',
  fontWeight: 700,
  color: theme.palette.text.secondary,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  marginBottom: theme.spacing(0.35),
}));

const AbilityValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'MedievalSharp',
  fontSize: '1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: 1.2,
}));

const TraitBlock = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  fontFamily: 'Merriweather',
  fontSize: '0.85rem',
  lineHeight: 1.55,
  color: theme.palette.text.primary,
}));

function MonsterHero({ monster, portraitSrc }) {
  const ac = formatArmorClass(monster.armor_class);
  const hp = formatHitPoints(monster);
  const speed = formatSpeed(monster.speed);

  return (
    <HeroRow>
      <HeroLeft>
        {portraitSrc ? (
          <PortraitFrame>
            <PortraitImg src={portraitSrc} alt="" />
          </PortraitFrame>
        ) : (
          <PortraitFrame
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'background.main',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'MedievalSharp',
                fontSize: '2rem',
                color: 'text.secondary',
                opacity: 0.35,
              }}
            >
              ?
            </Typography>
          </PortraitFrame>
        )}
      </HeroLeft>
      <HeroRight>
        <TopStatGrid>
          <TopStatCell>
            <TopStatLabel>Armor Class</TopStatLabel>
            <TopStatValue>{ac ?? '—'}</TopStatValue>
          </TopStatCell>
          <TopStatCell>
            <TopStatLabel>Hit Points</TopStatLabel>
            <TopStatValue>{hp ?? '—'}</TopStatValue>
          </TopStatCell>
          <TopStatCell>
            <TopStatLabel>Speed</TopStatLabel>
            <TopStatValue>{speed ?? '—'}</TopStatValue>
          </TopStatCell>
        </TopStatGrid>

        <CreatureSub>{formatCreatureTypeLine(monster)}</CreatureSub>
      </HeroRight>
    </HeroRow>
  );
}

function DetailSection({ monster }) {
  const detailRows = [
    getSavingThrowsEntry(monster.proficiencies),
    getSkillsEntry(monster.proficiencies),
    getCommaListEntry('Damage Vulnerabilities', monster.damage_vulnerabilities),
    getCommaListEntry('Damage Resistances', monster.damage_resistances),
    getCommaListEntry('Damage Immunities', monster.damage_immunities),
    getCommaListEntry('Condition Immunities', monster.condition_immunities),
    getSensesEntry(monster.senses),
    getLanguagesEntry(monster),
    getChallengeEntry(monster),
  ].filter(Boolean);

  return (
    <>
      <Divider sx={{ my: 1 }}>Details</Divider>
      {detailRows.length > 0 && (
        <DetailBlock sx={{ mb: 0.5 }}>
          {detailRows.map((row, i) => (
            <DetailRow key={`${row.label}-${i}`}>
              <DetailLabel component="div">{row.label}</DetailLabel>
              <DetailValue component="div">{row.value}</DetailValue>
            </DetailRow>
          ))}
        </DetailBlock>
      )}
    </>
  );
}

function AbilitiesSection({ monster }) {
  return (
    <>
      <AbilitySection>
        <Divider sx={{ my: 1 }}>Ability Scores</Divider>
        <AbilityGrid>
          {formatAbilityScores(monster).map(({ abbr, score, mod }) => (
            <AbilityCell key={abbr}>
              <AbilityAbbr>{abbr}</AbilityAbbr>
              <AbilityValue component="div">
                {score}{' '}
                <Box component="span" sx={{ fontSize: '0.88em', opacity: 0.92 }}>
                  ({mod})
                </Box>
              </AbilityValue>
            </AbilityCell>
          ))}
        </AbilityGrid>
      </AbilitySection>
    </>
  );
}

function NamedSection({ title, entries, emptyNote }) {
  const blocks = formatNamedBlocks(entries, emptyNote);
  if (!blocks.length) return null;
  return (
    <>
      <Divider sx={{ my: 1 }}>{title}</Divider>
      {blocks.map((block, i) => (
        <TraitBlock key={`${title}-${i}`} sx={{ mb: 2 }}>
          {block.name && (
            <Typography component="span" sx={{ fontWeight: 700, fontStyle: 'italic', marginRight: 1}}>
              {block.name}.{' '}
            </Typography>
          )}
          {block.desc.split(/\n\n+/).map((para, j) => (
            <React.Fragment key={j}>
              {j > 0 && <br />}
              {para}
            </React.Fragment>
          ))}
        </TraitBlock>
      ))}
    </>
  );
}

function FallbackStatBlock({ coin }) {
  const portrait = coin?.image;
  const ac = coin?.armorClass;
  const hp =
    coin?.currentHealth != null && coin?.maxHealth != null
      ? `${coin.currentHealth}/${coin.maxHealth}`
      : coin?.maxHealth != null
        ? String(coin.maxHealth)
        : null;

  return (
    <HeroRow sx={{ mb: 1 }}>
      {portrait && (
        <HeroLeft sx={{ flex: '1 1 160px', alignItems: 'flex-start' }}>
          <PortraitFrame>
            <PortraitImg src={portrait} alt="" />
          </PortraitFrame>
        </HeroLeft>
      )}

      <HeroRight>
        <TopStatGrid sx={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', mt: 2}}>
          {ac != null ? (
            <TopStatCell>
              <TopStatLabel>Armor Class</TopStatLabel>
              <TopStatValue>{ac}</TopStatValue>
            </TopStatCell>
          ) : (
            <TopStatCell>
              <TopStatLabel>Armor Class</TopStatLabel>
              <TopStatValue>—</TopStatValue>
            </TopStatCell>
          )}
          {hp != null ? (
            <TopStatCell>
              <TopStatLabel>Hit Points</TopStatLabel>
              <TopStatValue>{hp}</TopStatValue>
            </TopStatCell>
          ) : (
            <TopStatCell>
              <TopStatLabel>Hit Points</TopStatLabel>
              <TopStatValue>—</TopStatValue>
            </TopStatCell>
          )}
        </TopStatGrid>
      </HeroRight>
    </HeroRow>
  );
}

export default function MonsterInfoModal({ open, onClose, rawMonster, coin }) {
  const title = rawMonster?.name || coin?.name || 'Monster';
  const portrait = rawMonster?.image || coin?.image;

  return (
    <GGDialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <GGDialogTitle>{title}</GGDialogTitle>
      <Body>
        {!rawMonster && <FallbackStatBlock coin={coin} />}
        {rawMonster && (
          <>
            <MonsterHero monster={rawMonster} portraitSrc={portrait} />
            <AbilitiesSection monster={rawMonster} />
            <NamedSection title="Actions" entries={rawMonster.actions} />
            <NamedSection title="Traits" entries={rawMonster.special_abilities} />
            
            {rawMonster.reactions?.length > 0 && (
              <NamedSection title="Reactions" entries={rawMonster.reactions} />
            )}
            {rawMonster.legendary_actions?.length > 0 && (
              <NamedSection title="Legendary Actions" entries={rawMonster.legendary_actions} />
            )}

            <DetailSection monster={rawMonster} />
          </>
        )}
      </Body>
    </GGDialog>
  );
}
